import { useEffect, useMemo, useRef, useState } from 'react';
import WebMercatorViewport from 'viewport-mercator-project';
import { InlineLoading } from '@carbon/react';
import { Map, PolygonArea } from 'components/map';
import { WellMapLayer } from 'components/well-page-content/well-map-layer';
import { buildPolygonStyle, calculateBounds } from 'lib/map';
import { useSlotGeometry } from './hooks';
import { buildComparisonMapConfigAtom, MATCH_COLORS } from './map-config';
import { NOT_IN_EVAL } from './property-attributes';
import type { ComparisonResult } from './types';
import style from './property-mini-maps.module.scss';

const MAP_HEIGHT = 300;
const MAX_ZOOM = 14;
const VIEWPORT_PADDING = 24;

// Neutral development-area boundary drawn under the well for context.
const UNIT_STYLE = buildPolygonStyle({
  color: '#8d8d8d',
  fillOpacity: 0.12,
  lineOpacity: 0.5,
  lineWidth: 1,
});

/**
 * Only renders `children` once the container has scrolled into view (and
 * unmounts when it leaves), keeping the number of live Mapbox/WebGL contexts
 * bounded when many property maps are shown at once ("Show all maps"). Renders a
 * fixed-height placeholder otherwise so scroll position stays stable.
 * @param {object} props - children to mount when visible
 * @return {JSX.Element} The lazily-mounted content or a placeholder
 */
const LazyVisible = ({ children }: { children: React.ReactNode }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { rootMargin: '300px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={style.lazy}>
      {visible ? (
        children
      ) : (
        <div className={style.placeholder}>
          <InlineLoading description="Map loads on scroll…" />
        </div>
      )}
    </div>
  );
};

/**
 * One side's map (Origin or Target). Renders the property's well geometry over
 * its unit boundary, or a "Not in package eval" placeholder when the property is
 * absent on this side.
 * @param {object} props - side label, presence, geometry, and view state
 * @return {JSX.Element} The side map or placeholder
 */
const SideMap = ({
  label,
  present,
  result,
  well,
  unit,
  viewState,
  loading,
}: {
  label: string;
  present: boolean;
  result: ComparisonResult;
  well: ReturnType<typeof useSlotGeometry>['well'];
  unit: ReturnType<typeof useSlotGeometry>['unit'];
  viewState: { longitude: number; latitude: number; zoom: number } | null;
  loading: boolean;
}) => {
  const configAtom = useMemo(() => buildComparisonMapConfigAtom(), []);

  return (
    <div className={style.side}>
      <p className={style.sideLabel}>{label}</p>
      <div className={style.map}>
        {!present ? (
          <div className={style.empty}>{NOT_IN_EVAL}</div>
        ) : viewState ? (
          <Map
            longitude={viewState.longitude}
            latitude={viewState.latitude}
            zoom={viewState.zoom}
            mapConfigAtom={configAtom}
            showControls
          >
            {unit && (
              <PolygonArea
                id={`mini-unit-${result.uniqueSlotId}-${label}`}
                geometry={unit.geometry}
                style={UNIT_STYLE}
                showBorder
              />
            )}
            {(well?.wellBores ?? []).map((wb, idx) => (
              <WellMapLayer
                key={`${result.uniqueSlotId}-${label}-${wb.uwi14 ?? idx}`}
                id={`mini-well-${result.uniqueSlotId}-${label}-${idx}`}
                wellbore={wb}
                mapConfigAtom={configAtom}
                colorOverride={MATCH_COLORS[result.matchStatus]}
              />
            ))}
          </Map>
        ) : (
          <div className={style.empty}>
            <InlineLoading
              description={loading ? 'Loading geometry…' : 'No geometry'}
            />
          </div>
        )}
      </div>
    </div>
  );
};

interface PropertyMiniMapsProps {
  result: ComparisonResult;
}

/**
 * The in-view content of a property card: fetches the property's well + unit
 * geometry and renders the Origin/Target maps. Kept as its own component so the
 * fetch (useSlotGeometry) runs only once LazyVisible mounts it — i.e. when the
 * card scrolls into view — instead of firing for every result up front when
 * "Show all maps" is toggled on. Each side shows the geometry when the property
 * exists there, or "Not in package eval" when it does not.
 * @param {PropertyMiniMapsProps} props - The property's comparison result
 * @return {JSX.Element} The two-up Origin/Target maps
 */
const PropertyMiniMapsContent = ({ result }: PropertyMiniMapsProps) => {
  const { well, unit, loading } = useSlotGeometry(
    result.uwi,
    result.developmentAreaName
  );
  const containerRef = useRef<HTMLDivElement>(null);

  const originPresent =
    result.matchStatus === 'matched' || result.matchStatus === 'unmatched_a';
  const targetPresent =
    result.matchStatus === 'matched' || result.matchStatus === 'unmatched_b';

  const viewState = useMemo(() => {
    const coords: number[][] = [];
    (well?.wellBores ?? []).forEach((wb) => {
      if (wb.surfaceHole) coords.push(wb.surfaceHole.coordinates);
      if (wb.bottomHole) coords.push(wb.bottomHole.coordinates);
    });
    if (unit) {
      const ring = (unit.geometry as GeoJSON.Polygon).coordinates?.[0];
      if (Array.isArray(ring)) ring.forEach((c) => coords.push(c));
    }
    if (coords.length === 0) return null;
    try {
      const bounds = calculateBounds(coords);
      const width = (containerRef.current?.clientWidth ?? 800) / 2;
      const mercator = new WebMercatorViewport({ width, height: MAP_HEIGHT });
      const vs = mercator.fitBounds(
        [
          [bounds.getWest(), bounds.getSouth()],
          [bounds.getEast(), bounds.getNorth()],
        ],
        { padding: VIEWPORT_PADDING }
      );
      return {
        longitude: vs.longitude,
        latitude: vs.latitude,
        zoom: Math.min(vs.zoom, MAX_ZOOM),
      };
    } catch {
      return null;
    }
  }, [well, unit]);

  return (
    <div ref={containerRef} className={style.maps}>
      <SideMap
        label="Origin"
        present={originPresent}
        result={result}
        well={well}
        unit={unit}
        viewState={viewState}
        loading={loading}
      />
      <SideMap
        label="Target"
        present={targetPresent}
        result={result}
        well={well}
        unit={unit}
        viewState={viewState}
        loading={loading}
      />
    </div>
  );
};

/**
 * The Origin vs Target side-by-side maps for a single property card (mock v16).
 * Lazily mounts its content only while the card is in view, so the geometry
 * fetches fire on scroll-in rather than for every result at once.
 * @param {PropertyMiniMapsProps} props - The property's comparison result
 * @return {JSX.Element} The lazily-mounted two-up Origin/Target maps
 */
export const PropertyMiniMaps = ({ result }: PropertyMiniMapsProps) => (
  <LazyVisible>
    <PropertyMiniMapsContent result={result} />
  </LazyVisible>
);
